import React, { useState, lazy, Suspense, useEffect } from "react";
import { OutlineItem, GeneratedDataItem } from "./types";
import ContentRenderer from "./ContentRenderer";
import { SectionBlock } from "../StretchableViewPresentation";
import {
  FaArrowLeft,
  FaPlus,
  FaMinus,
  FaChartLine,
  FaChartBar,
  FaChartPie,
} from "react-icons/fa";
import { Theme } from "../../../lib/theme";
import ChartComponent from "./ChartComponent";
import PeopleComponent from "./PeopleComponent";
import Table from "../custom-builder/Table";
import TextAndImageComponent from "./TextAndImageComponent";
import TextComponent from "./TextComponent";
import GraphComponent from "./GraphComponent";
import ImageComponent from "./ImageComponent";
import axios from "axios";
import { toast } from "react-toastify";
import { text } from "stream/consumers";

// Lazily load the refine panels
const RewriteRefinePanel = lazy(() => import("./RewriteRefinePanel"));
const ToneAudiencePanel = lazy(() => import("./ToneAudiencePanel"));

// Define available section types
type DocumentGenerationSlideTypes =
  | "Text"
  | "Table"
  | "Image"
  | "Chart"
  | "People"
  | "TextandImage";

// Define generation methods
type GenerationMethods =
  | "Quick Generate"
  | "Section Narrative"
  | "Custon Builder";

interface SectionRendererProps {
  outlineSection: OutlineItem;
  sectionData: GeneratedDataItem[];
  editMode: Record<string, boolean>;
  editData: Record<string, any>;
  toggleEditMode: (id: string) => void;
  saveChanges: (id: string) => void;
  cancelEdit: (id: string) => void;
  handleInputChange: (
    id: string,
    field: string,
    value: any,
    componentType?: string
  ) => void;
  saving: Record<string, boolean>;
  editable: boolean;
  handleRefineClick?: (text?: string) => void; // Updated to accept text parameter
  onAddNewOutline?: (title: string, afterOutlineID: string) => void;
  onDeleteOutline?: (outlineID: string, type: string) => void;
  documentID?: string; // Add documentID prop
  onRefreshOutlines?: () => void; // Add refresh callback
  outlineIndex?: number; // new: index of this section in the outline array (0-based)
}

const SectionRenderer: React.FC<SectionRendererProps> = ({
  outlineSection,
  sectionData,
  editMode,
  editData,
  toggleEditMode,
  saveChanges,
  cancelEdit,
  handleInputChange,
  saving,
  editable,
  handleRefineClick,
  onAddNewOutline,
  onDeleteOutline,
  documentID,
  onRefreshOutlines,
  outlineIndex, // destructure new prop
}) => {
  // New state to manage refine panel visibility and settings
  const [showRefinePanel, setShowRefinePanel] = useState(false);
  const [activeRefineItemId, setActiveRefineItemId] = useState<string | null>(
    null
  );
  const [selectedItemText, setSelectedItemText] = useState<string>("");
  const [refineMode, setRefineMode] = useState<"rewrite" | "tone">("rewrite");

  // State for new component form
  const [showAddForm, setShowAddForm] = useState<string | null>(null); // itemId after which to add
  const [newComponentTitle, setNewComponentTitle] = useState("");
  const [newComponentType, setNewComponentType] =
    useState<DocumentGenerationSlideTypes>("Text");
  const [newComponentMethod, setNewComponentMethod] =
    useState<GenerationMethods>("Quick Generate");
  const orgId = sessionStorage.getItem("orgId");

  // State for temporary components
  const [tempComponents, setTempComponents] = useState<
    Array<{
      id: string;
      title: string;
      type: DocumentGenerationSlideTypes;
      method: GenerationMethods;
      afterItemId: string;
      outlineID: string;
      generatedOutlineID?: string;
    }>
  >([]);

  // State for showing generation screens
  const [showGenerationScreen, setShowGenerationScreen] = useState<{
    tempId: string;
    method: GenerationMethods;
    type: DocumentGenerationSlideTypes;
    title: string;
  } | null>(null);

  // Add state for inline generation screen
  const [inlineGenerationScreen, setInlineGenerationScreen] = useState<{
    afterItemId: string;
    method: GenerationMethods;
    type: DocumentGenerationSlideTypes;
    title: string;
  } | null>(null);

  // State for custom builder data
  const [customBuilderData, setCustomBuilderData] = useState<
    Record<string, any>
  >({});

  // State for API loading
  const [isGenerating, setIsGenerating] = useState<Record<string, boolean>>({});
  
  // State to track recently added components to prevent them from disappearing
  const [recentlyAddedComponents, setRecentlyAddedComponents] = useState<Set<string>>(new Set());
  
  // State for loading skeletons
  const [loadingSkeletons, setLoadingSkeletons] = useState<Array<{
    id: string;
    title: string;
    type: DocumentGenerationSlideTypes;
    afterItemId: string;
  }>>([]);

  // State for section narrative
  const [narrativeInput, setNarrativeInput] = useState("");
  const [narrativeImages, setNarrativeImages] = useState<string[]>([]);
  const [lastComponentType, setLastComponentType] =
    useState<DocumentGenerationSlideTypes | null>(null);

  const [isDialogVisibleQuick, setIsDialogVisibleQuick] = useState(false);

  // Clear images when component type changes
  useEffect(() => {
    if (
      inlineGenerationScreen &&
      lastComponentType &&
      lastComponentType !== inlineGenerationScreen.type
    ) {
      setNarrativeImages([]);
    }
    if (inlineGenerationScreen) {
      setLastComponentType(inlineGenerationScreen.type);
    }
  }, [inlineGenerationScreen?.type, lastComponentType]);

  if (sectionData.length === 0) return null;

  // Check if any of the section data items have a component_type that might create a duplicate heading
  const hasHeadingComponent = sectionData.some(
    (item) =>
      item.component_type === "Text" &&
      item.text?.title === outlineSection.title
  );

  // Handle refine action for a specific item
  const handleItemRefine = (itemId: string, itemText: string) => {
    // If the same item is clicked again, toggle the panel
    if (activeRefineItemId === itemId && showRefinePanel) {
      setShowRefinePanel(false);
      setActiveRefineItemId(null);
    } else {
      setActiveRefineItemId(itemId);
      setSelectedItemText(itemText);
      setShowRefinePanel(true);

      // Also call the parent's handleRefineClick if needed
      if (handleRefineClick) {
        handleRefineClick(itemText);
      }
    }
  };

  // Close refine panel
  const closeRefinePanel = () => {
    setShowRefinePanel(false);
    setActiveRefineItemId(null);
  };

  // Handle add new component
  const handleAddComponent = (afterItemId: string) => {
    setShowAddForm(afterItemId);
    setNewComponentTitle("");
    setNewComponentType("Text");
    setNewComponentMethod("Quick Generate");
  };

  // Cancel add form
  const cancelAddForm = () => {
    setShowAddForm(null);
    setNewComponentTitle("");
    setNewComponentType("Text");
    setNewComponentMethod("Quick Generate");
  };

  // Submit new component
  const submitNewComponent = async () => {
    if (!newComponentTitle.trim()) {
      alert("Please enter a title for the new component");
      return;
    }

    // Debug: Log the title being submitted
    console.log("Submitting new component:", {
      title: newComponentTitle,
      method: newComponentMethod,
      type: newComponentType,
    });

    if (newComponentMethod === "Quick Generate") {
      await handleQuickGenerateAPI();
    } else {
      setInlineGenerationScreen({
        afterItemId: showAddForm || "",
        method: newComponentMethod,
        type: newComponentType,
        title: newComponentTitle,
      });
      setLastComponentType(newComponentType);
      setShowAddForm(null);
    }
  };

  // Handle Custom Builder API call
  const handleCustomBuilderAPI = async () => {
    const authToken = sessionStorage.getItem("authToken");
    const API_URL =
      process.env.REACT_APP_BACKEND_URL ||
      "http://localhost:5001";

    if (!authToken || !orgId) {
      toast.error("Authentication required");
      return;
    }

    const tempId = `temp_${Date.now()}`;
    setIsGenerating((prev) => ({ ...prev, [tempId]: true }));
    
    // Add loading skeleton immediately
    const componentTitle = inlineGenerationScreen?.title || newComponentTitle;
    const componentType = inlineGenerationScreen?.type || newComponentType;
    const loadingSkeleton = {
      id: tempId,
      title: componentTitle,
      type: componentType as DocumentGenerationSlideTypes,
      afterItemId: inlineGenerationScreen?.afterItemId || showAddForm || "",
    };
    setLoadingSkeletons(prev => [...prev, loadingSkeleton]);

    try {
      toast.info(
        `Generating ${componentTitle}...`,
        {
          position: "top-right",
          autoClose: 3000,
        }
      );

      // FIX: use correct componentType (was accidentally using newComponentTitle)
      const actualComponentType = inlineGenerationScreen?.type || newComponentType;

      // Additional runtime validation for Image type (safety before API call)
      if (actualComponentType === "Image") {
        let imageUrls: string[] = [];
        if (Array.isArray(narrativeImages)) {
          imageUrls = narrativeImages;
        }
        if (imageUrls.length < 1) {
          toast.error(
            "Please upload at least 1 image for Image custom builder."
          );
          // Remove loading skeleton on validation failure
          setLoadingSkeletons(prev => prev.filter(skeleton => skeleton.id !== tempId));
          setIsGenerating((prev) => ({ ...prev, [tempId]: false }));
          return;
        }
        if (imageUrls.length > 4) {
          toast.error("Maximum 4 images allowed.");
          // Remove loading skeleton on validation failure
          setLoadingSkeletons(prev => prev.filter(skeleton => skeleton.id !== tempId));
          setIsGenerating((prev) => ({ ...prev, [tempId]: false }));
          return;
        }
      }

      const newOutlineID = `outlineID-${window.crypto.randomUUID()}`;
      // NOTE: for Custom Builder we must use the current section position (no +1)
      const newPosition =
        typeof outlineIndex === "number"
          ? outlineIndex
          : Math.max(
              0,
              sectionData.findIndex(
                (item) =>
                  item._id ===
                  (inlineGenerationScreen?.afterItemId || showAddForm)
              )
            );

      const sectionTypeParam =
        actualComponentType.toLowerCase() === "textandimage"
          ? "textandimage"
          : actualComponentType.toLowerCase() === "chart"
          ? "graphs"
          : actualComponentType.toLowerCase() === "table"
          ? "tables"
          : actualComponentType.toLowerCase();

      // Build payload based on component type
      let payload: any = {
        type: actualComponentType,
        title: componentTitle,
        documentID: documentID,
        outlineID: newOutlineID,
        position: newPosition,
        data: {
          slideName: componentTitle,
          title: componentTitle,
          outlineID: newOutlineID,
        },
      };

      // Add component-specific data based on reference implementations
      if (actualComponentType === "Text") {
        payload.data.text = customBuilderData.text || "";
        payload.data.input = "";
        // Ensure the title is preserved - try multiple field names that backend might expect
        payload.data.textTitle = componentTitle;
        payload.data.header = componentTitle;
        payload.data.header1 = componentTitle;
        payload.data.slideTitle = componentTitle;
        // Also ensure root level title is preserved
        payload.slideTitle = componentTitle;
        payload.header = componentTitle;
      } else if (actualComponentType === "Chart") {
        const seriesData: { key: string; value: string[] }[] = [];
        (customBuilderData.headers || ["", ""]).forEach((header: string, index: number) => {
          const values = (customBuilderData.rows || []).map(
            (row: any) => row[Object.keys(row)[index]] || ""
          );
          if (values.some((value: string) => value !== "")) {
            seriesData.push({ key: header, value: values });
          }
        });
        payload.data.chartType = (
          customBuilderData.selectedChart || "Bar"
        ).toLowerCase();
        payload.data.chart = { series: seriesData };
      } else if (actualComponentType === "Table") {
        const tableData = customBuilderData.tableData || {
          columnHeaders: ["", ""],
          rowHeaders: ["", ""],
          rows: [["", ""]],
        };
        const rowHeaders = tableData.rowHeaders.filter(
          (header: string) => header !== ""
        );
        const columnHeaders = tableData.columnHeaders.filter(
          (header: string) => header !== ""
        );

        payload.data = {
          ...payload.data,
          ...rowHeaders.reduce(
            (acc: any, header: string, index: number) => ({
              ...acc,
              [`rowHeader${index + 1}`]: header,
            }),
            {}
          ),
          ...columnHeaders.reduce(
            (acc: any, header: string, index: number) => ({
              ...acc,
              [`columnHeader${index}`]: header,
            }),
            {}
          ),
          ...tableData.rows.reduce(
            (acc: any, row: string[], index: number) => ({
              ...acc,
              [`rows${index + 1}`]: {
                attribute1: row[0] || "",
                attribute2: row[1] || "",
                attribute3: row[2] || "",
                attribute4: row[3] || "",
                attribute5: row[4] || "",
              },
            }),
            {}
          ),
        };
      } else if (actualComponentType === "People") {
        const peopleCount = customBuilderData.peopleCount || 2;
        const people = Array.from({ length: peopleCount }, (_, index) => ({
          name: customBuilderData[`person${index + 1}Name`] || "",
          designation: customBuilderData[`person${index + 1}Designation`] || "",
          company: customBuilderData[`person${index + 1}Company`] || "",
          description: customBuilderData[`person${index + 1}Description`] || "",
          image: "",
        }));
        payload.data.people = people;
      } else if (actualComponentType === "TextandImage") {
        payload.data.text = customBuilderData.textContent || "";
        payload.data.image = customBuilderData.imageUrl || "";
      } else if (actualComponentType === "Image") {
        // Normalize imageUrls from customBuilderData
        let imageUrls: string[] = [];
        if (Array.isArray(narrativeImages)) {
          imageUrls = narrativeImages;
        }
        //  else if (typeof narrativeImages === 'string' && customBuilderData.imageUrls.trim() !== '') {
        //   imageUrls = customBuilderData.imageUrls.split(',').map((u: string) => u.trim()).filter(Boolean);
        // }
        // limit to 4 just to be safe
        if (imageUrls.length > 4) imageUrls = imageUrls.slice(0, 4);
        payload.data = {
          ...payload.data,
          header1: imageUrls[0]
            ? imageUrls[0].split("/").pop()?.split("_")[0] || "header1"
            : "header1",
          header2: "image",
          imageurl: imageUrls,
        };
      }

      // Debug: Log the payload being sent to backend
      console.log("Custom Builder API Payload:", {
        endpoint: `${API_URL}/api/v1/data/documentcustom/generate-document/${orgId}/${sectionTypeParam}`,
        payload: payload,
        userEnteredTitle: componentTitle,
        componentType: actualComponentType,
      });

      const response = await axios.post(
        `${API_URL}/api/v1/data/documentcustom/generate-document/${orgId}/${sectionTypeParam}`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
            "Content-Type": "application/json",
          },
        }
      );

      toast.success(`${componentTitle} generated successfully!`, {
        position: "top-right",
        autoClose: 3000,
      });

      // Keep skeleton visible and refresh data
      if (onRefreshOutlines) {
        onRefreshOutlines();
      }
      
      // Remove skeleton after delay to allow data to load
      setTimeout(() => {
        setLoadingSkeletons(prev => prev.filter(skeleton => skeleton.id !== tempId));
      }, 2000);

      // Reset form and custom builder data
      setInlineGenerationScreen(null);
      setShowAddForm(null);
      setNewComponentTitle("");
      setNewComponentType("Text");
      setNewComponentMethod("Quick Generate");
      setCustomBuilderData({});
    } catch (error) {
      console.error("Error generating component:", error);
      toast.error("Failed to generate component. Please try again.", {
        position: "top-right",
        autoClose: 3000,
      });
      // Remove loading skeleton on error
      setLoadingSkeletons(prev => prev.filter(skeleton => skeleton.id !== tempId));
    } finally {
      setIsGenerating((prev) => ({ ...prev, [tempId]: false }));
    }
  };

  // Handle Section Narrative API call
  const handleSectionNarrativeAPI = async () => {
    const authToken = sessionStorage.getItem("authToken");
    const API_URL =
      process.env.REACT_APP_BACKEND_URL ||
      "http://localhost:5001";

    if (!authToken || !orgId) {
      toast.error("Authentication required");
      return;
    }

    if (inlineGenerationScreen?.type !== "Image" && !narrativeInput.trim()) {
      toast.error("Please provide narrative context");
      return;
    }

    const tempId = `temp_${Date.now()}`;
    setIsGenerating((prev) => ({ ...prev, [tempId]: true }));
    
    // Add loading skeleton immediately
    const loadingSkeleton = {
      id: tempId,
      title: newComponentTitle,
      type: newComponentType,
      afterItemId: inlineGenerationScreen?.afterItemId || showAddForm || "",
    };
    setLoadingSkeletons(prev => [...prev, loadingSkeleton]);

    try {
      toast.info(`Generating ${newComponentTitle}...`, {
        position: "top-right",
        autoClose: 3000,
      });

      const newOutlineID = `outlineID-${window.crypto.randomUUID()}`;
      const newPosition =
        typeof outlineIndex === "number"
          ? outlineIndex
          : sectionData.findIndex((item) => item._id === showAddForm) + 2;

      const payload: any = {
        input: inlineGenerationScreen?.type === "Image" ? "" : narrativeInput,
        type: newComponentType,
        title: newComponentTitle,
        documentID: documentID,
        outlineID: newOutlineID,
        position: newPosition,
      };

      // Add images for Image and TextandImage types only if images exist
      if (
        (newComponentType === "Image" || newComponentType === "TextandImage") &&
        narrativeImages.length > 0
      ) {
        if (newComponentType === "TextandImage") {
          // TextandImage: single image
          payload.data = {
            image: narrativeImages[0],
          };
        } else if (newComponentType === "Image") {
          // Image: multiple images (max 4)
          payload.data = {
            images: narrativeImages.slice(0, 4),
          };
        }
      }

      const response = await axios.post(
        `${API_URL}/api/v1/data/documentnarrative/generate-document/${orgId}`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
            "Content-Type": "application/json",
          },
        }
      );

      toast.success(`${newComponentTitle} generated successfully!`, {
        position: "top-right",
        autoClose: 3000,
      });

      // Track this component as recently added
      setRecentlyAddedComponents(prev => new Set([...prev, newOutlineID]));
      
      // Remove from recently added after 10 seconds
      setTimeout(() => {
        setRecentlyAddedComponents(prev => {
          const newSet = new Set(prev);
          newSet.delete(newOutlineID);
          return newSet;
        });
      }, 10000);

      // Refresh data immediately and remove skeleton after delay
      if (onRefreshOutlines) {
        onRefreshOutlines();
      }
      
      setTimeout(() => {
        setLoadingSkeletons(prev => prev.filter(skeleton => skeleton.id !== tempId));
      }, 4000); // Wait 4 seconds for data to load

      // Reset form and narrative data
      setInlineGenerationScreen(null);
      setShowAddForm(null);
      setNewComponentTitle("");
      setNewComponentType("Text");
      setNewComponentMethod("Quick Generate");
      setNarrativeInput("");
      setNarrativeImages([]);
    } catch (error) {
      console.error("Error generating component:", error);
      toast.error("Failed to generate component. Please try again.", {
        position: "top-right",
        autoClose: 3000,
      });
      // Remove loading skeleton on error
      setLoadingSkeletons(prev => prev.filter(skeleton => skeleton.id !== tempId));
    } finally {
      setIsGenerating((prev) => ({ ...prev, [tempId]: false }));
    }
  };

  // Handle Quick Generate API call
  const handleQuickGenerateAPI = async () => {
    const authToken = sessionStorage.getItem("authToken");
    const orgId = sessionStorage.getItem("orgId");
    const API_URL =
      process.env.REACT_APP_BACKEND_URL ||
      "http://localhost:5001";

    if (!authToken || !orgId) {
      toast.error("Authentication required");
      return;
    }

    const tempId = `temp_${Date.now()}`;
    setIsGenerating((prev) => ({ ...prev, [tempId]: true }));
    
    // Add loading skeleton immediately
    const loadingSkeleton = {
      id: tempId,
      title: newComponentTitle,
      type: newComponentType,
      afterItemId: showAddForm || "",
    };
    setLoadingSkeletons(prev => [...prev, loadingSkeleton]);

    try {
      toast.info(`Generating ${newComponentTitle}...`, {
        position: "top-right",
        autoClose: 3000,
      });

      const newOutlineID = `outlineID-${window.crypto.randomUUID()}`;
      // For Quick Generate use current section position (no +1)
      const newPosition =
        typeof outlineIndex === "number"
          ? outlineIndex
          : Math.max(
              0,
              sectionData.findIndex((item) => item._id === showAddForm)
            );

      const response = await axios.post(
        `${API_URL}/api/v1/data/generatedocument/generate-document/${orgId}`,
        {
          input: `Extract/Generate relevant data for the text "${newComponentTitle}"`,
          type: newComponentType,
          title: newComponentTitle,
          documentID: documentID,
          outlineID: newOutlineID,
          position: newPosition,
        },
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
            "Content-Type": "application/json",
          },
        }
      );

      toast.success(`${newComponentTitle} generated successfully!`, {
        position: "top-right",
        autoClose: 3000,
      });

      // Track this component as recently added
      setRecentlyAddedComponents(prev => new Set([...prev, newOutlineID]));
      
      // Remove from recently added after 10 seconds
      setTimeout(() => {
        setRecentlyAddedComponents(prev => {
          const newSet = new Set(prev);
          newSet.delete(newOutlineID);
          return newSet;
        });
      }, 10000);

      // Refresh data immediately and remove skeleton after delay
      if (onRefreshOutlines) {
        onRefreshOutlines();
      }
      
      setTimeout(() => {
        setLoadingSkeletons(prev => prev.filter(skeleton => skeleton.id !== tempId));
      }, 4000); // Wait 4 seconds for data to load

      // Reset form
      setShowAddForm(null);
      setNewComponentTitle("");
      setNewComponentType("Text");
      setNewComponentMethod("Quick Generate");
    } catch (error) {
      console.error("Error generating component:", error);
      toast.error("Failed to generate component. Please try again.", {
        position: "top-right",
        autoClose: 3000,
      });
      // Remove loading skeleton on error
      setLoadingSkeletons(prev => prev.filter(skeleton => skeleton.id !== tempId));
    } finally {
      setIsGenerating((prev) => ({ ...prev, [tempId]: false }));
    }
  };

  // Handler to go back to add form from generation screen
  const handleBackToAddForm = () => {
    setInlineGenerationScreen(null);
    setShowAddForm(inlineGenerationScreen?.afterItemId || null);
  };

  // Delete temporary component
  // const deleteTempComponent = (tempId: string) => {
  //   const tempComp = tempComponents.find(comp => comp.id === tempId);
  //   if (tempComp && onDeleteOutline && tempComp.generatedOutlineID) {
  //     onDeleteOutline(tempComp.generatedOutlineID);
  //   }
  //   // Remove from temp components
  //   setTempComponents(prev => prev.filter(comp => comp.id !== tempId));
  //   // Close generation screen if open for this component
  //   if (showGenerationScreen?.tempId === tempId) {
  //     setShowGenerationScreen(null);
  //   }
  // };

  // Close generation screen
  const closeGenerationScreen = () => {
    setShowGenerationScreen(null);
  };

  // Helper to handle custom builder input changes
  const handleCustomBuilderChange = (
    id: string,
    field: string,
    value: any,
    componentType?: string
  ) => {
    setCustomBuilderData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const getValidationTooltip = () => {
    const componentType = inlineGenerationScreen?.type;

    if (componentType === "Table") {
      return "Table requires at least 2 rows, 2 columns, and all cells filled.";
    }

    if (componentType === "Image") {
      return "Please upload 1 to 4 images.";
    }

    if (componentType === "TextandImage") {
      return "Both text content and at least 1 image are required.";
    }

    if (componentType === "Text") {
      return "Text content cannot be empty.";
    }

    if (componentType === "People") {
      return "All added people should have name and description fields filled or remove the people. Minimum 2 people required.";
    }

    if (componentType === "Chart") {
      return "Chart requires 1–3 series, headers filled, and at least 3 rows of valid data.";
    }

    return "Please complete the required fields.";
  };

  // Validation function for custom builder components
  const isCustomBuilderValid = () => {
    const componentType = inlineGenerationScreen?.type;

    if (componentType === "Table") {
      const tableData = customBuilderData.tableData || {
        columnHeaders: ["", ""],
        rowHeaders: ["", ""],
        rows: [
          ["", ""],
          ["", ""],
        ],
      };

      // Check if all mandatory fields are filled (minimum 2 rows and 2 columns)
      const areMandatoryFieldsFilled =
        tableData.columnHeaders.length >= 2 &&
        tableData.rowHeaders.length >= 2 &&
        tableData.columnHeaders
          .slice(0, 2)
          .every((header: string) => header.trim() !== "") &&
        tableData.rowHeaders
          .slice(0, 2)
          .every((header: string) => header.trim() !== "");

      // Check if all rendered fields (headers and table cells) are filled
      const areAllFieldsFilled =
        tableData.columnHeaders.every((header: string) => header.trim() !== "") &&
        tableData.rowHeaders.every((header: string) => header.trim() !== "") &&
        tableData.rows.every((row: string[]) => row.every((cell: string) => cell.trim() !== ""));

      return areMandatoryFieldsFilled && areAllFieldsFilled;
    }

    // Add validation for Image custom builder: require 1..4 images
    if (componentType === "Image") {
      let imageUrls: string[] = [];
      if (Array.isArray(narrativeImages)) {
        imageUrls = narrativeImages;
      }
      //  else if (typeof customBuilderData.imageUrls === 'string' && customBuilderData.imageUrls.trim() !== '') {
      //   imageUrls = customBuilderData.imageUrls.split(',').map((u: string) => u.trim()).filter(Boolean);
      // }
      return imageUrls.length >= 1 && imageUrls.length <= 4;
    }

    if (componentType === "TextandImage") {
      let imageUrls: string[] = [];
      return (
        customBuilderData.textContent?.trim()?.length > 0 &&
        customBuilderData.imageUrl?.trim()?.length > 0
      );
    }
    if (componentType === "Text") {
      return customBuilderData.text?.trim()?.length > 0;
    }
    if (componentType === "People") {
      const peopleCount = customBuilderData.peopleCount || 2;
      if (peopleCount < 2 || peopleCount > 5) return false;
      
      // Check that at least 2 people have all required fields filled
      let validPeopleCount = 0;
      for (let i = 1; i <= peopleCount; i++) {
        const name = customBuilderData[`person${i}Name`]?.trim();
        const designation = customBuilderData[`person${i}Designation`]?.trim();
        const company = customBuilderData[`person${i}Company`]?.trim();
        const description = customBuilderData[`person${i}Description`]?.trim();
        
        if (name && designation && company && description) {
          validPeopleCount++;
        }
      }
      
      return validPeopleCount >= 2;
    }

    if (componentType === "Chart") {
      const seriesCount = customBuilderData.series || 2;
      if (seriesCount < 1 || seriesCount > 3) return false;
      const headers = customBuilderData.headers || [];
      if (headers.length < seriesCount) return false;
      if (
        !headers.slice(0, seriesCount).every((h: string) => h.trim().length > 0)
      )
        return false;
      const rows = customBuilderData.rows || [];

      const allFilled = rows.every((row: any) =>
        Object.values(row).every(
          (v) => typeof v === "string" && v.trim().length > 0
        )
      );
      if (rows.length < 3 && !allFilled) return false;
    }

    return true; // Other component types are always valid
  };

  // Helper to render empty component for custom builder
  const renderEmptyCustomComponent = (type: DocumentGenerationSlideTypes) => {
    const handleChartClick = (chartType: string) => {
      setCustomBuilderData((prev) => ({
        ...prev,
        selectedChart: chartType,
        currentScreen: "inputScreen",
        headers: ["", ""],
        rows: [
          { label: "", services: "" },
          { label: "", services: "" },
        ],
        series: 2,
      }));
    };

    const addSeries = () => {
      const maxSeries = customBuilderData.selectedChart === "Pie" ? 1 : 3;
      const currentSeries = customBuilderData.series || 2;
      if (currentSeries < maxSeries) {
        const currentRows = customBuilderData.rows || [
          { label: "", services: "" },
          { label: "", services: "" },
        ];
        const updatedRows = currentRows.map((row: any) => ({
          ...row,
          [`series${currentSeries + 1}`]: "",
        }));
        setCustomBuilderData((prev) => ({
          ...prev,
          series: currentSeries + 1,
          headers: [...(prev.headers || ["", ""]), ""],
          rows: updatedRows,
        }));
      }
    };

    const addRow = () => {
      const currentRows = customBuilderData.rows || [
        { label: "", services: "" },
        { label: "", services: "" },
      ];
      if (currentRows.length < 10) {
        setCustomBuilderData((prev) => ({
          ...prev,
          rows: [...currentRows, { label: "", services: "" }],
        }));
      }
    };

    const removeLastRow = () => {
      const currentRows = customBuilderData.rows || [
        { label: "", services: "" },
        { label: "", services: "" },
      ];
      if (currentRows.length > 2) {
        setCustomBuilderData((prev) => ({
          ...prev,
          rows: currentRows.slice(0, -1),
        }));
      }
    };

    const handleAddColumn = () => {
      const tableData = customBuilderData.tableData || {
        columnHeaders: ["", ""],
        rowHeaders: ["", ""],
        rows: [
          ["", ""],
          ["", ""],
        ],
      };
      if (tableData.columnHeaders.length < 5) {
        setCustomBuilderData((prev) => ({
          ...prev,
          tableData: {
            ...tableData,
            columnHeaders: [...tableData.columnHeaders, ""],
            rows: tableData.rows.map((row: string[]) => [...row, ""]),
          },
        }));
      }
    };

    const handleAddRow = () => {
      const tableData = customBuilderData.tableData || {
        columnHeaders: ["", "",""],
        rowHeaders: ["", ""],
        rows: [
          ["", ""],
          ["", ""],
        ],
      };
      if (tableData.rows.length < 8) {
        setCustomBuilderData((prev) => ({
          ...prev,
          tableData: {
            ...tableData,
            rows: [
              ...tableData.rows,
              Array(tableData.columnHeaders.length-1).fill(""),
            ],
            rowHeaders: [...tableData.rowHeaders, ""],
          },
        }));
      }
    };

    switch (type) {
      case "Chart":
        return (
          <div className="flex flex-col h-full w-full lg:p-4 p-2">
            {/* <h3>Graphs</h3> */}
            {/* <div className="w-full lg:p-1">
              <input
                type="text"
                value={inlineGenerationScreen?.title || ""}
                readOnly
                className="border w-full mt-2 text-[#091220] md:text-lg rounded-md font-semibold bg-transparent p-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div> */}
            {!customBuilderData.currentScreen ||
            customBuilderData.currentScreen === "chartSelection" ? (
              <div className="w-full h-full flex-row lg:flex-col lg:ml-1 ml-2 mt-2">
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 md:gap-4 mt-4 mr-2 md:mr-5 h-20 md:h-28">
                  {["Line", "Bar", "Pie"].map((chart) => (
                    <div
                      key={chart}
                      className="flex flex-col items-center justify-center border border-gray-300  w-full h-full px-4 py-2 lg:py-4 rounded-md cursor-pointer"
                      onClick={() => handleChartClick(chart)}
                    >
                      {chart === "Line" && (
                        <FaChartLine className="h-12 w-12 sm:h-16 sm:w-16 text-[#3667B2]" />
                      )}
                      {chart === "Bar" && (
                        <FaChartBar className="h-12 w-12 sm:h-16 sm:w-16 text-green-700" />
                      )}
                      {chart === "Pie" && (
                        <FaChartPie className="h-12 w-12 sm:h-16 sm:w-16 text-orange-600" />
                      )}
                      <span className="text-lg text-weight-500 sm:text-base">
                        {chart}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="flex flex-col w-full h-full bg-white overflow-x-auto scrollbar-none lg:p-1">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="text-lg font-semibold">
                    {customBuilderData.selectedChart} Chart Data
                  </h4>
                  <button
                    onClick={() =>
                      setCustomBuilderData((prev) => ({
                        ...prev,
                        currentScreen: "chartSelection",
                      }))
                    }
                    className="text-sm text-blue-600 hover:text-blue-800"
                  >
                    Change Chart Type
                  </button>
                </div>
                <div className="mt-4 overflow-x-auto overflow-y-auto scrollbar-none">
                  <table className="table-auto border-collapse w-full min-w-[600px]">
                    <thead>
                      <tr className="bg-[#F5F7FA]">
                        {(customBuilderData.headers || ["", ""]).map(
                          (header: string, index: number) => (
                            <th
                              key={index}
                              className="px-2 py-2 text-left lg:px-4"
                            >
                              <input
                                type="text"
                                value={header}
                                placeholder={`Enter Series ${index + 1}`}
                                onChange={(e) => {
                                  const newHeaders = [
                                    ...(customBuilderData.headers || ["", ""]),
                                  ];
                                  newHeaders[index] = e.target.value;
                                  setCustomBuilderData((prev) => ({
                                    ...prev,
                                    headers: newHeaders,
                                  }));
                                }}
                                className="px-2 py-1 font-medium rounded w-full min-w-[100px] outline-none placeholder-[#5D5F61] placeholder:font-medium bg-transparent"
                              />
                            </th>
                          )
                        )}
                        {(customBuilderData.series || 2) <
                          (customBuilderData.selectedChart === "Pie"
                            ? 1
                            : 3) && (
                          <th className="py-2">
                            <button
                              onClick={addSeries}
                              className="flex items-center px-2 sm:px-4 py-1 sm:py-2 bg-[#F5F7FA] text-black font-medium transition rounded-md text-sm"
                            >
                              <FaPlus className="mr-1 sm:mr-2" />
                              <span className="hidden sm:inline">Add Series</span>
                              <span className="sm:hidden">Add</span>
                            </button>
                          </th>
                        )}
                        {(customBuilderData.series || 2) > 2 && (
                          <th className="py-2">
                            <button
                              onClick={() => {
                                const currentSeries =
                                  customBuilderData.series || 2;
                                if (currentSeries > 2) {
                                  setCustomBuilderData((prev) => ({
                                    ...prev,
                                    series: currentSeries - 1,
                                    headers: (prev.headers || ["", ""]).slice(
                                      0,
                                      -1
                                    ),
                                  }));
                                }
                              }}
                              className="flex items-center px-2 sm:px-4 py-1 sm:py-2 bg-red-100 text-red-700 font-medium transition rounded-md hover:bg-red-200 text-sm"
                            >
                              <FaMinus className="mr-1 sm:mr-2" />
                              <span className="hidden sm:inline">Remove Series</span>
                              <span className="sm:hidden">Remove</span>
                            </button>
                          </th>
                        )}
                      </tr>
                    </thead>
                    <tbody>
                      {(
                        customBuilderData.rows || [
                          { label: "", services: "" },
                          { label: "", services: "" },
                        ]
                      ).map((row: any, rowIndex: number) => (
                        <tr key={rowIndex}>
                          <td className="px-2 py-2 lg:px-4">
                            <input
                              type="text"
                              value={row.label}
                              placeholder={`Enter Data ${rowIndex + 1}`}
                              onChange={(e) => {
                                const newRows = [
                                  ...(customBuilderData.rows || [
                                    { label: "", services: "" },
                                    { label: "", services: "" },
                                  ]),
                                ];
                                newRows[rowIndex].label = e.target.value;
                                setCustomBuilderData((prev) => ({
                                  ...prev,
                                  rows: newRows,
                                }));
                              }}
                              className="px-2 py-1 rounded w-full min-w-[100px] outline-none"
                            />
                          </td>
                          {(customBuilderData.series || 2) >= 2 && (
                            <td className="px-2 py-2 lg:px-4">
                              <input
                                type="text"
                                value={row.services}
                                placeholder={`Enter Data ${rowIndex + 1}`}
                                onChange={(e) => {
                                  const newRows = [
                                    ...(customBuilderData.rows || [
                                      { label: "", services: "" },
                                      { label: "", services: "" },
                                    ]),
                                  ];
                                  newRows[rowIndex].services = e.target.value;
                                  setCustomBuilderData((prev) => ({
                                    ...prev,
                                    rows: newRows,
                                  }));
                                }}
                                className="px-2 py-1 rounded w-full min-w-[100px] outline-none"
                              />
                            </td>
                          )}
                          {Array.from({
                            length: Math.max(
                              0,
                              (customBuilderData.series || 2) - 2
                            ),
                          }).map((_, seriesIndex) => {
                            const seriesKey = `series${seriesIndex + 3}`;
                            return (
                              <td
                                key={seriesIndex}
                                className="px-2 py-2 lg:px-4"
                              >
                                <input
                                  type="text"
                                  value={row[seriesKey] || ""}
                                  placeholder={`Enter Data ${rowIndex + 1}`}
                                  onChange={(e) => {
                                    const newRows = [
                                      ...(customBuilderData.rows || [
                                        { label: "", services: "" },
                                        { label: "", services: "" },
                                      ]),
                                    ];
                                    newRows[rowIndex][seriesKey] =
                                      e.target.value;
                                    setCustomBuilderData((prev) => ({
                                      ...prev,
                                      rows: newRows,
                                    }));
                                  }}
                                  className="px-2 py-1 rounded w-full min-w-[100px] outline-none"
                                />
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="flex gap-2 mt-4">
                  <button
                    onClick={addRow}
                    disabled={(customBuilderData.rows || []).length >= 10}
                    className={`flex items-center px-2 sm:px-3 py-2 transition text-sm ${
                      (customBuilderData.rows || []).length >= 10
                        ? "bg-[#E1E3E5] text-[#5D5F61] cursor-not-allowed"
                        : "bg-white text-[#5D5F61] hover:bg-[#3667B2] hover:text-white border border-gray-300 rounded-lg"
                    }`}
                  >
                    <FaPlus className="mr-1 sm:mr-2" />
                    <span className="hidden sm:inline">Add Data</span>
                    <span className="sm:hidden">Add</span>
                  </button>
                  <button
                    onClick={removeLastRow}
                    disabled={(customBuilderData.rows || []).length <= 2}
                    className={`flex items-center px-2 sm:px-3 py-2 transition text-sm ${
                      (customBuilderData.rows || []).length <= 2
                        ? "bg-[#E1E3E5] text-[#5D5F61] cursor-not-allowed"
                        : "bg-white text-[#5D5F61] hover:bg-red-500 hover:text-white border border-gray-300 rounded-lg"
                    }`}
                  >
                    <FaMinus className="mr-1 sm:mr-2" />
                    <span className="hidden sm:inline">Remove Data</span>
                    <span className="sm:hidden">Remove</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        );
      case "Table":
        return (
          <div className="flex flex-col w-full h-full lg:p-4 p-2">
            {/* <h3 className="text-semibold">Table</h3> */}
            {/* <div className="w-full p-1">
              <input
                type="text"
                value={inlineGenerationScreen?.title || ""}
                readOnly
                className="border w-full mt-2 md:text-lg rounded-md font-semibold bg-transparent p-2 pr-10 focus:outline-none focus:ring-2"
              />
            </div> */}
            <div className="flex-1 lg:overflow-x-auto overflow-auto scrollbar-none md:p-1 p-1">
              <div className="lg:overflow-y-auto max-h-[calc(100vh-150px)] w-full overflow-x-auto scrollbar-none">
                <table className="table-auto w-full">
                  <thead>
                    <tr>
                      <th className="bg-gray-100 p-2 lg:w-max overflow-x-auto">
                        <input
                          type="text"
                          placeholder="Enter Column 0"
                          value={(customBuilderData.tableData?.columnHeaders || ["", "",""])[0] || ""}
                          onChange={(e) => {
                            const tableData = customBuilderData.tableData || {
                              columnHeaders: ["", "",""],
                              rowHeaders: ["", ""],
                              rows: [
                                ["", ""],
                                ["", ""],
                              ],
                            };
                            const newHeaders = [...tableData.columnHeaders];
                            newHeaders[0] = e.target.value;
                            setCustomBuilderData((prev) => ({
                              ...prev,
                              tableData: {
                                ...tableData,
                                columnHeaders: newHeaders,
                              },
                            }));
                          }}
                          className="w-full font-semibold text-center border-none bg-transparent focus:outline-none"
                        />
                      </th>
                      {(
                        customBuilderData.tableData?.columnHeaders || ["", "",""]
                      ).slice(1).map((header: string, index: number) => (
                        <th
                          key={index + 1}
                          className="bg-gray-100 p-1 md:p-2 min-w-[120px] md:min-w-[150px]"
                        >
                          <input
                            type="text"
                            placeholder={`Enter Column ${index + 1}`}
                            value={header}
                            onChange={(e) => {
                              const tableData = customBuilderData.tableData || {
                                columnHeaders: ["", "",""],
                                rowHeaders: ["", ""],
                                rows: [
                                  ["", ""],
                                  ["", ""],
                                ],
                              };
                              const newHeaders = [...tableData.columnHeaders];
                              newHeaders[index + 1] = e.target.value;
                              setCustomBuilderData((prev) => ({
                                ...prev,
                                tableData: {
                                  ...tableData,
                                  columnHeaders: newHeaders,
                                },
                              }));
                            }}
                            className="w-full font-semibold text-center border-none bg-transparent focus:outline-none"
                          />
                        </th>
                      ))}
                      <th className="bg-gray-50 lg:p-2 p-1">
                        <div className="flex justify-center gap-2">
                          <button
                            onClick={handleAddColumn}
                            disabled={
                              (customBuilderData.tableData?.columnHeaders || [])
                                .length >= 5
                            }
                            className="bg-white flex items-center justify-center border border-gray-300 rounded-full p-1 hover:bg-blue-100 disabled:opacity-50"
                          >
                            <FaPlus />
                          </button>
                          <button
                            onClick={() => {
                              const tableData = customBuilderData.tableData || {
                                columnHeaders: ["", "",""],
                                rowHeaders: ["", ""],
                                rows: [["", ""]],
                              };
                              if (tableData.columnHeaders.length > 2) {
                                setCustomBuilderData((prev) => ({
                                  ...prev,
                                  tableData: {
                                    ...tableData,
                                    columnHeaders:
                                      tableData.columnHeaders.slice(0, -1),
                                    rows: tableData.rows.map((row: string[]) =>
                                      row.slice(0, -1)
                                    ),
                                  },
                                }));
                              }
                            }}
                            disabled={
                              (customBuilderData.tableData?.columnHeaders || [])
                                .length <= 2
                            }
                            className="bg-white flex items-center justify-center border border-gray-300 rounded-full p-1 hover:bg-red-100 disabled:opacity-50"
                          >
                            <FaMinus />
                          </button>
                        </div>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {(() => {
                      const tableData = customBuilderData.tableData || {
                        columnHeaders: ["", "",""],
                        rowHeaders: ["", ""],
                        rows: [
                          ["", ""],
                          ["", ""],
                        ],
                      };
                      return tableData.rows.map((row: string[], rowIndex: number) => (
                        <tr key={rowIndex}>
                          <td className="bg-gray-100 lg:min-w-[0vw] overflow-x-auto">
                            <input
                              type="text"
                              placeholder={`Enter Row ${rowIndex + 1}`}
                              value={tableData.rowHeaders[rowIndex] || ""}
                              onChange={(e) => {
                                const newRowHeaders = [...tableData.rowHeaders];
                                newRowHeaders[rowIndex] = e.target.value;
                                setCustomBuilderData((prev) => ({
                                  ...prev,
                                  tableData: {
                                    ...tableData,
                                    rowHeaders: newRowHeaders,
                                  },
                                }));
                              }}
                              className="w-full font-medium text-center border-none bg-transparent focus:outline-none"
                            />
                          </td>
                          {row.map((cell: string, colIndex: number) => (
                            <td
                              key={colIndex}
                              className="border border-gray-300 p-2"
                            >
                              <input
                                type="text"
                                placeholder={`Cell ${rowIndex + 1}-${
                                  colIndex + 1
                                }`}
                                value={cell || ""}
                                onChange={(e) => {
                                  const newRows = tableData.rows.map(
                                    (r: string[], rIdx: number) =>
                                      rIdx === rowIndex
                                        ? r.map((c: string, cIdx: number) =>
                                            cIdx === colIndex
                                              ? e.target.value
                                              : c
                                          )
                                        : r
                                  );
                                  setCustomBuilderData((prev) => ({
                                    ...prev,
                                    tableData: {
                                      ...tableData,
                                      rows: newRows,
                                    },
                                  }));
                                }}
                                className="w-full text-start border-none bg-transparent focus:outline-none"
                              />
                            </td>
                          ))}
                        </tr>
                      ));
                    })()}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td className="bg-gray-50 p-1">
                        <div className="flex justify-center gap-2">
                          <button
                            onClick={handleAddRow}
                            disabled={
                              (customBuilderData.tableData?.rows || [])
                                .length >= 8
                            }
                            className="bg-white flex items-center justify-center border border-gray-300 rounded-full p-1 hover:bg-blue-100 disabled:opacity-50"
                          >
                            <FaPlus />
                          </button>
                          <button
                            onClick={() => {
                              const tableData = customBuilderData.tableData || {
                                columnHeaders: ["", "",""],
                                rowHeaders: ["", ""],
                                rows: [
                                  ["", ""],
                                  ["", ""],
                                ],
                              };
                              if (tableData.rows.length > 2) {
                                setCustomBuilderData((prev) => ({
                                  ...prev,
                                  tableData: {
                                    ...tableData,
                                    rows: tableData.rows.slice(0, -1),
                                    rowHeaders: tableData.rowHeaders.slice(
                                      0,
                                      -1
                                    ),
                                  },
                                }));
                              }
                            }}
                            disabled={
                              (customBuilderData.tableData?.rows || [])
                                .length <= 2
                            }
                            className="bg-white flex items-center justify-center border border-gray-300 rounded-full p-1 hover:bg-red-100 disabled:opacity-50"
                          >
                            <FaMinus />
                          </button>
                        </div>
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          </div>
        );
      case "Image":
        return (
          <div className="mb-2">
            <ImageComponent
              item={
                {
                  _id: "narrative_image_Image",
                  component_type: "Image",
                  image: "",
                } as any
              }
              editMode={true}
              editData={{ image: narrativeImages.join(",") }}
              handleInputChange={(id, field, value) => {
                if (field === "image") {
                  setNarrativeImages(
                    value ? value.split(",").filter(Boolean) : []
                  );
                }
              }}
            />
          </div>
        );
      case "People":
        return (
          <div className="flex flex-col w-full h-full p-2 lg:p-4">
            {/* <h3 className="text-semibold">People</h3> */}
            {/* <div className="w-full p-1">
              <input
                type="text"
                value={inlineGenerationScreen?.title || ""}
                readOnly
                className="border w-full mt-2 text-[#091220] md:text-lg rounded-md font-semibold bg-transparent p-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div> */}
            <div className="flex-1 overflow-y-auto scrollbar-none md:mt-1">
              {Array.from(
                { length: customBuilderData.peopleCount || 2 },
                (_, index) => (
                  <div
                    key={index}
                    className="flex flex-row w-full items-center gap-2"
                  >
                    <div className="flex flex-col gap-2 lg:gap-4 mb-2 p-1 w-full">
                      <div className="grid grid-cols-1 lg:grid-cols-3 gap-2 lg:gap-4 w-full mt-2">
                        <input
                          type="text"
                          placeholder={`Enter Name ${index + 1}`}
                          value={
                            customBuilderData[`person${index + 1}Name`] || ""
                          }
                          onChange={(e) =>
                            setCustomBuilderData((prev) => ({
                              ...prev,
                              [`person${index + 1}Name`]: e.target.value,
                            }))
                          }
                          className="p-2 border border-gray-300 rounded-md lg:rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                        <input
                          type="text"
                          placeholder={`Enter Designation ${index + 1}`}
                          value={
                            customBuilderData[
                              `person${index + 1}Designation`
                            ] || ""
                          }
                          onChange={(e) =>
                            setCustomBuilderData((prev) => ({
                              ...prev,
                              [`person${index + 1}Designation`]: e.target.value,
                            }))
                          }
                          className="p-2 border border-gray-300 rounded-md lg:rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                        <input
                          type="text"
                          placeholder={`Enter Company ${index + 1}`}
                          value={
                            customBuilderData[`person${index + 1}Company`] || ""
                          }
                          onChange={(e) =>
                            setCustomBuilderData((prev) => ({
                              ...prev,
                              [`person${index + 1}Company`]: e.target.value,
                            }))
                          }
                          className="p-2 border border-gray-300 rounded-md lg:rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                      <input
                        type="text"
                        placeholder={`Enter Description ${index + 1}`}
                        value={
                          customBuilderData[`person${index + 1}Description`] ||
                          ""
                        }
                        onChange={(e) =>
                          setCustomBuilderData((prev) => ({
                            ...prev,
                            [`person${index + 1}Description`]: e.target.value,
                          }))
                        }
                        className="w-full p-2 border border-gray-300 rounded-md lg:rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                      {index === (customBuilderData.peopleCount || 2) - 1 && (
                        <button
                          onClick={() => {
                            const currentCount =
                              customBuilderData.peopleCount || 2;
                            if (currentCount < 6) {
                              setCustomBuilderData((prev) => ({
                                ...prev,
                                peopleCount: currentCount + 1,
                              }));
                            }
                          }}
                          disabled={(customBuilderData.peopleCount || 2) >= 6}
                          className={`flex w-1/2 lg:w-[180px] items-center justify-center gap-x-2 py-2 md:border md:border-gray-300 md:rounded-lg text-[#5D5F61] ${
                            (customBuilderData.peopleCount || 2) >= 6
                              ? "bg-[#E1E3E5] text-[#5D5F61] cursor-not-allowed"
                              : "bg-white text-[#5D5F61] hover:bg-[#3667B2] hover:text-white"
                          }`}
                        >
                          <FaPlus />
                          Add More People
                        </button>
                      )}
                    </div>
                    <button
                      onClick={() => {
                        const currentCount = customBuilderData.peopleCount || 2;
                        if (currentCount > 2) {
                          setCustomBuilderData((prev) => ({
                            ...prev,
                            peopleCount: currentCount - 1,
                          }));
                        }
                      }}
                      disabled={(customBuilderData.peopleCount || 2) <= 2}
                      className={`${
                        (customBuilderData.peopleCount || 2) <= 2
                          ? "text-gray-400 cursor-not-allowed"
                          : "text-[#3667B2] hover:bg-red-100"
                      } bg-white flex items-center justify-center border border-[#E1E3E5] rounded-full w-6 h-6 p-2 transition`}
                    >
                      <FaMinus />
                    </button>
                  </div>
                )
              )}
            </div>
          </div>
        );
      case "TextandImage":
        return (
          <div className="flex flex-col w-full h-full p-2 lg:p-4">
            {/* <h3 className="text-semibold">Text and Image</h3> */}
            {/* <div className="w-full p-1">
              <input
                type="text"
                value={inlineGenerationScreen?.title || ""}
                readOnly
                className="border w-full mt-2 text-[#091220] md:text-lg rounded-md font-semibold bg-transparent p-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div> */}
            <div className="flex  flex-col lg:flex-row lg:items-start lg:justify-between gap-4 mt-4">
              <div className="flex flex-col lg:w-[50%]">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Text Content
                </label>
                <textarea
                  placeholder="Enter your text content here..."
                  value={customBuilderData.textContent || ""}
                  onChange={(e) => {
                    setCustomBuilderData((prev) => ({
                      ...prev,
                      textContent: e.target.value,
                    }));

                    // auto-resize logic
                    const textarea = e.target;
                    textarea.style.height = "auto"; // reset height
                    const maxHeight = 300; // max height in px
                    textarea.style.height = `${Math.min(
                      textarea.scrollHeight,
                      maxHeight
                    )}px`;
                  }}
                  className="flex w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 min-h-[100px] max-h-[300px] overflow-y-auto resize-none"
                />
              </div>
              <div className="flex flex-col lg:w-[50%] min-h-[100px]">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Image
                </label>
                <ImageComponent
                  item={
                    {
                      _id: "custom_textandimage",
                      component_type: "Image",
                      image: "",
                    } as any
                  }
                  editMode={true}
                  editData={{ image: customBuilderData.imageUrl || "" }}
                  handleInputChange={(id, field, value) => {
                    if (field === "image") {
                      setCustomBuilderData((prev) => ({
                        ...prev,
                        imageUrl: value,
                      }));
                    }
                  }}
                />
              </div>
            </div>
          </div>
        );
      default:
        return <div>No builder available for this type.</div>;
    }
  };

  return (
    <div
      key={outlineSection.outlineID}
      id={`section-${outlineSection.outlineID}`}
      className="scroll-section section pb-1 md:pb-2 transform transition-all duration-300 ease-out"
      data-section-id={outlineSection.outlineID}
      style={{
        scrollMarginTop: "10px",
        touchAction: "pan-y",
        WebkitOverflowScrolling: "touch"
      }}
    >
      {/* Only render the section title if there's no content item that would create a duplicate heading */}
      {/* {!hasHeadingComponent && (
        <h2 className="text-2xl font-bold mb-6 pt-4">{outlineSection.title}</h2>
      )} */}

      {(() => {
        // Helper to extract text from an item for refinement
        const getItemText = (it: GeneratedDataItem): string => {
          if (it.component_type === "Text" && it.text) {
            return typeof it.text === "object"
              ? it.text.text
              : (it.text as string);
          }
          if (it.component_type === "TextandImage" && it.textandimage) {
            return it.textandimage.text || "";
          }
          return "";
        };

        return sectionData.map((item, idx) => {
          const isTextLike =
            item.component_type === "Text" ||
            item.component_type === "TextandImage";
          const blockType: "TITLE-TEXT" | "TITLE-IMAGE" | "TITLE-TEXT-IMAGE" =
            item.component_type === "TextandImage"
              ? "TITLE-TEXT-IMAGE"
              : item.component_type === "Image" ||
                item.component_type === "Chart"
              ? "TITLE-IMAGE"
              : "TITLE-TEXT";
          const key = item._id || `${outlineSection.outlineID}-${idx}`;
          const itemId = item._id || "";

          return (
            <div key={key} className="mb-0 md:mb-1">
              <SectionBlock
                type={blockType}
                onAdd={editable ? () => handleAddComponent(itemId) : undefined}
                onEdit={editable ? () => toggleEditMode(itemId) : undefined}
                // provide onRefine only for text-like items and when editable
                onRefine={
                  editable && isTextLike
                    ? () => handleItemRefine(itemId, getItemText(item))
                    : undefined
                }
                onDelete={editable ? () =>
                  onDeleteOutline &&
                  onDeleteOutline(outlineSection.outlineID, outlineSection.type)
                : undefined}
              >
                <div
                  className={`${
                    activeRefineItemId === itemId && showRefinePanel
                      ? "flex flex-col lg:flex-row"
                      : ""
                  } w-full`}
                >
                  <div
                    className={`${
                      activeRefineItemId === itemId && showRefinePanel
                        ? "w-full lg:w-1/2 lg:pr-4"
                        : "w-full"
                    }`}
                  >
                    {/* Mark this item to hide image in child renderer when refine panel is active for it */}
                    {(() => {
                      const itemForRender = { ...item } as any;
                      if (activeRefineItemId === itemId && showRefinePanel)
                        itemForRender.__hideImage = true;
                      return (
                        <ContentRenderer
                          item={itemForRender}
                          editMode={editMode}
                          editData={editData}
                          toggleEditMode={toggleEditMode}
                          saveChanges={saveChanges}
                          cancelEdit={cancelEdit}
                          handleInputChange={handleInputChange}
                          saving={saving}
                          editable={editable}
                        />
                      );
                    })()}
                  </div>

                  {activeRefineItemId === itemId && showRefinePanel && (
                    <div className="w-full lg:w-1/2 lg:pl-4 mt-4 lg:mt-0 inline-refine-panel">
                      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-2 gap-2">
                        <div className="flex gap-2 w-full sm:w-auto">
                          <button
                            onClick={() => setRefineMode("rewrite")}
                            className={`px-2 sm:px-3 py-1 rounded text-sm ${
                              refineMode === "rewrite"
                                ? "bg-blue-500 text-white"
                                : "bg-gray-100"
                            }`}
                          >
                            Rewrite
                          </button>
                          <button
                            onClick={() => setRefineMode("tone")}
                            className={`px-2 sm:px-3 py-1 rounded text-sm ${
                              refineMode === "tone"
                                ? "bg-blue-500 text-white"
                                : "bg-gray-100"
                            }`}
                          >
                            <span className="hidden sm:inline">Tone & Audience</span>
                            <span className="sm:hidden">Tone</span>
                          </button>
                        </div>
                        <button
                          onClick={closeRefinePanel}
                          className="text-sm text-gray-600 self-end sm:self-auto"
                        >
                          Close
                        </button>
                      </div>

                      {/* intentionally removed the big original text preview per user request */}
                      {/* <div className="original-text-panel p-3 border rounded mb-3">{selectedItemText}</div> */}

                      <Suspense fallback={<div>Loading refine tools...</div>}>
                        {refineMode === "rewrite" ? (
                          <RewriteRefinePanel
                            selectedText={selectedItemText}
                            onBack={closeRefinePanel}
                            onAccept={(text) => {
                              const id = item._id || "";
                              if (!id) {
                                console.warn(
                                  "No item id available to save refined text"
                                );
                                return;
                              }
                              
                              // Update the selected text for future refine operations
                              setSelectedItemText(text);
                              
                              // Update the data directly using handleInputChange
                              const field = item.component_type === "TextandImage" 
                                ? "textandimage.text" 
                                : "text.text";
                              
                              console.log("RewriteRefinePanel - Updating text with refined content:", { id, field, text, componentType: item.component_type });
                              
                              // Ensure we're in edit mode first
                              if (!editMode[id]) {
                                toggleEditMode(id);
                              }
                              
                              // Use setTimeout to ensure edit mode is set before updating data
                              setTimeout(() => {
                                handleInputChange(id, field, text, item.component_type);
  
                                // Show success message
                                toast.success("Text updated! Click Save to persist changes.");
                              }, 100);
                            }}
                          />
                        ) : (
                          <ToneAudiencePanel
                            selectedText={selectedItemText}
                            onBack={closeRefinePanel}
                            onAccept={(text) => {
                              const id = item._id || "";
                              if (!id) {
                                console.warn(
                                  "No item id available to save refined text"
                                );
                                return;
                              }
                              
                              // Update the selected text for future refine operations
                              setSelectedItemText(text);
                              
                              // Update the data directly using handleInputChange
                              const field = item.component_type === "TextandImage" 
                                ? "textandimage.text" 
                                : "text.text";
                              
                              console.log("ToneAudiencePanel - Updating text with refined content:", { id, field, text, componentType: item.component_type });
                              
                              // Ensure we're in edit mode first
                              if (!editMode[id]) {
                                toggleEditMode(id);
                              }
                              
                              // Use setTimeout to ensure edit mode is set before updating data
                              setTimeout(() => {
                                handleInputChange(id, field, text, item.component_type);
                                // Show success message
                                toast.success("Text updated! Click Save to persist changes.");
                              }, 100);
                            }}
                          />
                        )}
                      </Suspense>
                    </div>
                  )}
                </div>
              </SectionBlock>

              {/* Add new component form - only show when editable */}
              {editable && showAddForm === itemId && !inlineGenerationScreen && (
                <div
                  className="mt-4 p-4 border border-gray-300 rounded-lg bg-gray-50"
                  onClick={(e) => e.stopPropagation()}
                  onMouseDown={(e) => e.stopPropagation()}
                  onKeyDown={(e) => e.stopPropagation()}
                >
                  <h4 className="font-semibold mb-3">Add New Component</h4>

                  <div className="space-y-3">
                    <div className="flex gap-3">
                      <div className="flex-1">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Title
                        </label>
                        <input
                          type="text"
                          value={newComponentTitle}
                          onChange={(e) => {
                            e.stopPropagation();
                            setNewComponentTitle(e.target.value);
                          }}
                          onFocus={(e) => e.stopPropagation()}
                          onBlur={(e) => e.stopPropagation()}
                          onKeyDown={(e) => {
                            e.stopPropagation();
                            if (e.key === "Enter" && newComponentTitle.trim()) {
                              e.preventDefault();
                              submitNewComponent();
                            }
                            if (e.key === "Escape") {
                              e.preventDefault();
                              cancelAddForm();
                            }
                          }}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="Enter component title"
                          autoComplete="off"
                          autoFocus
                        />
                      </div>

                      <div className="flex-1">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Section Type
                        </label>
                        <select
                          value={newComponentType}
                          onChange={(e) => {
                            e.stopPropagation();
                            setNewComponentType(
                              e.target.value as DocumentGenerationSlideTypes
                            );
                          }}
                          onFocus={(e) => e.stopPropagation()}
                          onBlur={(e) => e.stopPropagation()}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="Text">Text</option>
                          <option value="Table">Table</option>
                          <option value="Image">Image</option>
                          <option value="Chart">Chart</option>
                          <option value="People">People</option>
                          <option value="TextandImage">Text and Image</option>
                        </select>
                      </div>
                    </div>

                    {/* Generation Method Dropdown */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Generation Method
                      </label>
                      <select
                        value={newComponentMethod}
                        onChange={(e) => {
                          e.stopPropagation();
                          setNewComponentMethod(
                            e.target.value as GenerationMethods
                          );
                        }}
                        onFocus={(e) => e.stopPropagation()}
                        onBlur={(e) => e.stopPropagation()}
                        disabled={
                          !newComponentTitle.trim() || !newComponentType
                        }
                        className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                          !newComponentTitle.trim() || !newComponentType
                            ? "bg-gray-100 cursor-not-allowed"
                            : ""
                        }`}
                      >
                        <option value="Quick Generate">
                          Quick Generate (Instantly draft full documents or
                          slides in seconds with AI. Perfect for fast, polished
                          first versions.)
                        </option>
                        <option
                          value="Section Narrative"
                          disabled={newComponentType === "Image"}
                          className={
                            newComponentType === "Image"
                              ? "text-gray-400 cursor-not-allowed"
                              : ""
                          }
                        >
                          Section Narrative (Build each section step by step
                          with AI ensuring logical flow, clarity, and structured
                          storytelling. )
                        </option>
                        <option value="Custom Builder">
                          Custom Builder (Full creative control over structure,
                          tone, formatting, and style for truly tailored
                          documents or presentations.)
                        </option>
                      </select>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex gap-2 pt-2">
                      <button
                        onClick={submitNewComponent}
                        disabled={
                          Object.values(isGenerating).some(Boolean) ||
                          !newComponentTitle.trim()
                        }
                        className={`px-4 py-2 text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                          Object.values(isGenerating).some(Boolean) ||
                          !newComponentTitle.trim()
                            ? "bg-gray-400 cursor-not-allowed"
                            : "bg-blue-600 hover:bg-blue-700"
                        }`}
                      >
                        {Object.values(isGenerating).some(Boolean) ? (
                          <div className="flex items-center gap-2">
                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                            Generating...
                          </div>
                        ) : (
                          "Create Component"
                        )}
                      </button>
                      <button
                        onClick={cancelAddForm}
                        disabled={Object.values(isGenerating).some(Boolean)}
                        className={`px-4 py-2 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-500 ${
                          Object.values(isGenerating).some(Boolean)
                            ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                            : "bg-gray-300 text-gray-700 hover:bg-gray-400"
                        }`}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Inline generation screen for Section Narrative or Custom Builder - only show when editable */}
              {editable && inlineGenerationScreen &&
                inlineGenerationScreen.afterItemId === itemId && (
                  <div className="mt-4 p-4 border border-gray-300 rounded-lg bg-white">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-xl font-semibold">
                        {inlineGenerationScreen.method === "Section Narrative"
                          ? "Section Narrative"
                          : "Custom Builder"}{" "}
                        - {inlineGenerationScreen.title}
                      </h3>
                      <button
                        onClick={handleBackToAddForm}
                        className="flex items-center gap-1 text-gray-500 hover:text-gray-700"
                      >
                        <FaArrowLeft /> Back
                      </button>
                    </div>
                    {inlineGenerationScreen.method === "Section Narrative" ? (
                      <div className="flex flex-col p-2 lg:p-4 h-full">
                        {inlineGenerationScreen.type !== "Image" && (
                          <div className="mb-2">
                            {/* <label className="block font-semibold mb-1">
                              Slide Narrative
                            </label> */}
                            <textarea
                              value={narrativeInput}
                              onChange={(e) =>
                                setNarrativeInput(e.target.value)
                              }
                              placeholder="Please provide some context and narrative to generate this section."
                              className="w-full p-2 overflow-y-auto scrollbar-none rounded-md border focus:outline-none focus:ring-2 min-h-[120px] max-h-[300px] resize-y"
                              style={{
                                color: "#374151",
                                backgroundColor: "#ffffff",
                                borderColor: "#e5e7eb",
                                fontFamily: "inherit",
                                fontSize: "14px",
                                outline: "none",
                              }}
                            />
                          </div>
                        )}
                        {(inlineGenerationScreen.type === "Image" ||
                          inlineGenerationScreen.type === "TextandImage") && (
                          <div className="mb-2">
                            <ImageComponent
                              item={
                                {
                                  _id: `narrative_image_${inlineGenerationScreen.type}`,
                                  component_type: "Image",
                                  image: "",
                                } as any
                              }
                              editMode={true}
                              editData={{ image: narrativeImages.join(",") }}
                              handleInputChange={(id, field, value) => {
                                if (field === "image") {
                                  setNarrativeImages(
                                    value
                                      ? value.split(",").filter(Boolean)
                                      : []
                                  );
                                }
                              }}
                            />
                          </div>
                        )}
                        <div className="w-full flex items-center justify-start">
                          <button
                            onClick={handleSectionNarrativeAPI}
                            disabled={
                              Object.values(isGenerating).some(Boolean) ||
                              (inlineGenerationScreen.type !== "Image" &&
                                !narrativeInput.trim())
                            }
                            className={`w-full sm:w-auto px-4 py-2 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                              Object.values(isGenerating).some(Boolean) ||
                              (inlineGenerationScreen.type !== "Image" &&
                                !narrativeInput.trim())
                                ? "bg-gray-400 cursor-not-allowed"
                                : "bg-blue-600 hover:bg-blue-700"
                            } text-white`}
                          >
                            {Object.values(isGenerating).some(Boolean) ? (
                              <div className="flex items-center justify-center gap-2">
                                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                Generating...
                              </div>
                            ) : (
                              "Generate Section"
                            )}
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="py-4 max-h-[70vh] overflow-y-auto">
                        {/* Custom Builder Input Fields */}
                        {inlineGenerationScreen.type === "Text" ? (
                          <div className="space-y-4">
                            <div>
                              {/* <label className="block text-sm font-medium text-gray-700 mb-1">
                                Text Content
                              </label> */}
                              <textarea
                                value={customBuilderData.text || ""}
                                onChange={(e) =>
                                  setCustomBuilderData((prev) => ({
                                    ...prev,
                                    text: e.target.value,
                                  }))
                                }
                                placeholder="Enter the text content..."
                                className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 min-h-[100px]"
                              />
                            </div>
                          </div>
                        ) : (
                          <div className="mb-4 flex-1 overflow-y-auto">
                            {renderEmptyCustomComponent(
                              inlineGenerationScreen.type
                            )}
                          </div>
                        )}

                        <div
                          className=" relative  group w-full sm:w-auto"
                          onMouseEnter={() => {
                            if (
                              Object.values(isGenerating).some(Boolean) ||
                              !isCustomBuilderValid()
                            ) {
                              setIsDialogVisibleQuick(true);
                            }
                          }}
                          onMouseLeave={() => setIsDialogVisibleQuick(false)}
                        >
                          <button
                            onClick={handleCustomBuilderAPI}
                            disabled={
                              Object.values(isGenerating).some(Boolean) ||
                              !isCustomBuilderValid()
                            }
                            className={`w-full sm:w-auto px-4 py-2 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                              Object.values(isGenerating).some(Boolean) ||
                              !isCustomBuilderValid()
                                ? "bg-gray-400 cursor-not-allowed"
                                : "bg-blue-600 hover:bg-blue-700"
                            } text-white`}
                          >
                            {Object.values(isGenerating).some(Boolean) ? (
                              <div className="flex items-center gap-2">
                                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                Generating...
                              </div>
                            ) : (
                              <>
                                <span className="hidden sm:inline">Generate Component</span>
                                <span className="sm:hidden">Generate</span>
                              </>
                            )}
                          </button>

                          {isDialogVisibleQuick && (
                            <div
                              className="absolute left-0 top-0 w-[16rem] p-3 rounded-lg shadow-lg z-10"
                              style={{
                                backgroundColor: Theme.colors.stroke.weak,
                                color: Theme.colors.text.default,
                                transform: "translateY(-100%)", // move it above the button
                              }}
                            >
                              <p
                                className="text-xs text-center"
                                style={{ color: Theme.colors.text.weak }}
                              >
                                {Object.values(isGenerating).some(Boolean)
                                  ? "Please wait, generation is in progress."
                                  : getValidationTooltip()}
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}

              {/* Render loading skeletons - only show when editable */}
              {editable && loadingSkeletons
                .filter(skeleton => skeleton.afterItemId === itemId)
                .map(skeleton => (
                  <div key={skeleton.id} className="mt-4">
                    <div className="border border-gray-300 rounded-lg p-4 bg-gray-50 animate-pulse">
                      <div className="flex items-center gap-2 mb-3">
                        <div className="h-6 bg-gray-300 rounded w-1/3"></div>
                        <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">Generating...</span>
                      </div>
                      <div className="space-y-2">
                        <div className="h-4 bg-gray-300 rounded w-full"></div>
                        <div className="h-4 bg-gray-300 rounded w-3/4"></div>
                        <div className="h-4 bg-gray-300 rounded w-1/2"></div>
                      </div>
                      <div className="mt-3 flex items-center gap-2">
                        <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                        <span className="text-sm text-gray-600">Generating {skeleton.type} component...</span>
                      </div>
                    </div>
                  </div>
                ))}
            </div>
          );
        });
      })()}
    </div>
  );
};

export default SectionRenderer;
